import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronLeft,
  faChevronRight,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import marketplaceService from "../../services/marketplaceService";
import { AssetService } from "../../services/assetService";
import { AssetDiceTemplate, AssetTableItem } from "../../types/types";
import { useToast } from "../../components/ui";
import { formatMoney, MIN_PAID_PRICE_CENTS, MAX_PRICE_CENTS } from "../../utils/money";

interface PublishWizardProps {
  selectedAssets: AssetTableItem[];
  onClose: () => void;
  onSuccess: () => void;
}

const PublishWizard: React.FC<PublishWizardProps> = ({ selectedAssets, onClose, onSuccess }) => {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // Kept as a string: Number("") === 0, so a numeric state silently republished a
  // cleared field as a free bundle.
  const [priceInput, setPriceInput] = useState("0");
  const priceCents = Math.round(Number(priceInput) * 100);
  const priceError =
    priceInput.trim() === "" || !Number.isFinite(Number(priceInput))
      ? "Enter a price, or 0 for free."
      : priceCents !== 0 && (priceCents < MIN_PAID_PRICE_CENTS || priceCents > MAX_PRICE_CENTS)
        ? `Paid bundles must be between ${formatMoney(MIN_PAID_PRICE_CENTS)} and ${formatMoney(MAX_PRICE_CENTS)}.`
        : null;
  const [assetConfigs, setAssetConfigs] = useState(() =>
    selectedAssets.map((a) => ({
      assetId: a.id,
      name: a.name,
      sections: (a.sections || []).map((s) => ({ id: s.id, name: s.name, included: true })),
      diceTemplates: [] as Array<{ id: number; name: string; included: boolean }>,
    }))
  );
  const [isLoadingDiceTemplates, setIsLoadingDiceTemplates] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadDiceTemplates = async () => {
      try {
        const templatesByAsset = await Promise.all(
          selectedAssets.map(async (asset) => {
            let diceTemplates: AssetDiceTemplate[] = [];
            if (["Character", "Beast", "NPC"].includes(asset.type)) {
              diceTemplates = await AssetService.getAssetDiceTemplates(asset.id);
            }
            return {
              assetId: asset.id,
              diceTemplates,
            };
          })
        );

        if (!isMounted) return;

        setAssetConfigs((prev) =>
          prev.map((config) => {
            const entry = templatesByAsset.find((item) => item.assetId === config.assetId);
            return {
              ...config,
              diceTemplates: (entry?.diceTemplates || []).map((dt: AssetDiceTemplate) => ({
                id: dt.id,
                name: dt.name,
                included: true,
              })),
            };
          })
        );
      } catch {
        if (!isMounted) return;
        toast.showToast({
          type: "error",
          message:
            "Failed to load one or more dice templates. Close and reopen this wizard to retry.",
        });
      } finally {
        if (isMounted) {
          setIsLoadingDiceTemplates(false);
        }
      }
    };

    void loadDiceTemplates();

    return () => {
      isMounted = false;
    };
  }, [selectedAssets, toast]);

  const handleToggleSection = (assetIdx: number, sectionIdx: number) => {
    const next = [...assetConfigs];
    next[assetIdx].sections[sectionIdx].included = !next[assetIdx].sections[sectionIdx].included;
    setAssetConfigs(next);
  };

  const handleToggleDiceTemplate = (assetIdx: number, dtIdx: number) => {
    const next = [...assetConfigs];
    next[assetIdx].diceTemplates[dtIdx].included = !next[assetIdx].diceTemplates[dtIdx].included;
    setAssetConfigs(next);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        title,
        description,
        priceCents,
        assets: assetConfigs.map((ac) => ({
          assetId: ac.assetId,
          includedSectionIds: ac.sections.filter((s) => s.included).map((s) => s.id),
          includedDiceTemplateIds: ac.diceTemplates.filter((dt) => dt.included).map((dt) => dt.id),
        })),
      };

      await marketplaceService.publishListing(payload);
      toast.showToast({ type: "success", message: "Successfully published to marketplace!" });
      onSuccess();
    } catch {
      toast.showToast({ type: "error", message: "Failed to publish listing." });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <h2 className="text-lg font-medium text-white">Publish to Marketplace</h2>
            <p className="text-xs text-zinc-500">Step {step} of 2</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">
                  Listing Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-sm text-white focus:border-amber-400 focus:outline-none"
                  placeholder="e.g., The Lost Mines of Phandelver Asset Pack"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-sm text-white h-32 focus:border-amber-400 focus:outline-none"
                  placeholder="Tell others what's in this bundle..."
                />
              </div>
              <div>
                <label
                  htmlFor="listing-price"
                  className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1"
                >
                  Price (USD)
                </label>
                <input
                  id="listing-price"
                  type="number"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  className="w-24 bg-zinc-800 border border-zinc-700 rounded p-2 text-sm text-white focus:border-amber-400 focus:outline-none"
                  min="0"
                  step="0.01"
                />
                <span className="text-xs text-zinc-500 ml-2">
                  Set to 0 for free community content.
                </span>
                {priceError && <p className="mt-1 text-xs text-red-400">{priceError}</p>}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <p className="text-sm text-zinc-400">
                Fine-tune your assets. Uncheck attributes you don't want to include in the public
                listing.
              </p>
              {isLoadingDiceTemplates && (
                <p className="text-xs text-zinc-500">Loading dice templates...</p>
              )}
              {assetConfigs.map((ac, aIdx) => (
                <div
                  key={ac.assetId}
                  className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-800"
                >
                  <h3 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
                    {ac.name}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-[10px] font-bold text-zinc-500 uppercase mb-2">
                        Sections
                      </h4>
                      {ac.sections.length === 0 ? (
                        <p className="text-[10px] text-zinc-600">No sections</p>
                      ) : (
                        <div className="space-y-1">
                          {ac.sections.map((s, sIdx: number) => (
                            <label
                              key={s.id}
                              className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer hover:text-white"
                            >
                              <input
                                type="checkbox"
                                checked={s.included}
                                onChange={() => handleToggleSection(aIdx, sIdx)}
                                className="w-3 h-3 rounded bg-zinc-900 border-zinc-700 text-amber-500"
                              />
                              {s.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold text-zinc-500 uppercase mb-2">
                        Dice Templates
                      </h4>
                      {ac.diceTemplates.length === 0 ? (
                        <p className="text-[10px] text-zinc-600">No templates</p>
                      ) : (
                        <div className="space-y-1">
                          {ac.diceTemplates.map((dt, dtIdx: number) => (
                            <label
                              key={dt.id}
                              className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer hover:text-white"
                            >
                              <input
                                type="checkbox"
                                checked={dt.included}
                                onChange={() => handleToggleDiceTemplate(aIdx, dtIdx)}
                                className="w-3 h-3 rounded bg-zinc-900 border-zinc-700 text-amber-500"
                              />
                              {dt.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 flex justify-between">
          {step === 2 ? (
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              Back
            </button>
          ) : (
            <div />
          )}
          <button
            onClick={() => (step === 1 ? setStep(2) : handleSubmit())}
            disabled={step === 1 && (!title || priceError !== null)}
            className={`flex items-center gap-2 px-6 py-2 text-sm font-medium rounded transition-all ${
              step === 1 && (!title || priceError !== null)
                ? "bg-zinc-800 text-zinc-600 cursor-not-allowed"
                : "bg-amber-400 text-zinc-950 hover:bg-amber-300"
            }`}
          >
            {step === 1 ? "Next" : "Publish to Marketplace"}
            {step === 1 && <FontAwesomeIcon icon={faChevronRight} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishWizard;
